import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { api } from '@shared/api';

export function useCertificationCardPreview(
  href: string,
  viewerAccessScope: string,
  enabled = true
) {
  const [src, setSrc] = useState('');
  const query = useQuery({
    queryKey: ['crm-certification-card-preview', viewerAccessScope, href],
    queryFn: ({ signal }) =>
      api.getBlob(href, {
        signal,
        headers: { Accept: 'image/jpeg,image/png,image/webp' },
      }),
    enabled: enabled && href.length > 0 && viewerAccessScope.length > 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
  useEffect(() => {
    if (!query.data) {
      setSrc('');
      return;
    }
    const url = URL.createObjectURL(query.data);
    setSrc(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [query.data, viewerAccessScope]);
  return { error: query.error, src, isError: query.isError, refetch: query.refetch };
}

function cardExtension(blob: Blob) {
  if (blob.type === 'image/png') return 'png';
  if (blob.type === 'image/webp') return 'webp';
  return 'jpg';
}

/** Fetches the protected attachment through the authenticated shared transport. */
export function useCertificationCardDownload(href: string, viewerAccessScope: string) {
  return useMutation({
    mutationKey: ['crm-certification-card-download', viewerAccessScope, href],
    mutationFn: async () => {
      const blob = await api.getBlob(href, {
        headers: { Accept: 'image/jpeg,image/png,image/webp' },
        params: { disposition: 'attachment' },
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `certification-card.${cardExtension(blob)}`;
      link.href = url;
      link.click();
      window.setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1_000);
    },
  });
}
